import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import cn from 'classnames';
import moment from 'moment';
import PT from 'prop-types';

import CalendarLargeSVG from 'Components/Admin/assets/ CalendarLargeSVG';
import formatDate from 'Shared/util/formatDate';
import DayPicker from 'react-day-picker';
import CrossLargeSVG from 'Components/Admin/assets/CrossLargeSVG';

const NUMBER_OF_YEARS = 10;
const currentYear = new Date().getFullYear();
const fromMonth = new Date(currentYear, 0);
const toMonth = new Date(currentYear + 10, 11);

const YearMonthForm = ({ date, localeUtils, onChange }) => {
  const months = localeUtils.getMonths();

  const years = Array.from(Array(NUMBER_OF_YEARS), (_, i) => currentYear + i);

  const handleChange = function handleChange(e) {
    const { year, month } = e.target.form;
    onChange(new Date(year.value, month.value));
  };

  return (
    <form className="DayPicker-Caption">
      <div className="custom-select">
        <select name="month" onChange={handleChange} value={date.getMonth()}>
          {months.map((month, i) => (
            <option key={month} value={i}>
              {month}
            </option>
          ))}
        </select>
      </div>
      <div className="custom-select">
        <select name="year" onChange={handleChange} value={date.getFullYear()}>
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>
    </form>
  );
};

const Picker = ({ isOpen, isAlwaysOpened, open, value, close, setValue, parentSelector, closeOnLeave = true, showNavbar = true }) => {
  const [month, setMonth] = useState(value);
  const [timer, setTimer] = useState(null);
  const $parent = useRef(document.createElement('div'));
  const $container = useRef(null);
  const $picker = useRef(null);

  const handleClickOutside = (e) => {
    if (!$picker.current?.contains(e.target)) !isAlwaysOpened && close();
  };

  useEffect(() => {
    if (!isAlwaysOpened) {
      document.addEventListener('click', handleClickOutside);

      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, []);

  useEffect(() => {
    if (parentSelector && $parent.current) {
      $parent.current.classList.add('date-picker__renderer');
      $container.current = document.querySelector(parentSelector);
      $container.current.appendChild($parent.current);

      return () => {
        $container.current.removeChild($parent.current);
      };
    }
  }, [parentSelector]);

  useEffect(() => {
    value && setMonth(value);
  }, [value]);

  const handleYearMonthChange = (month) => setMonth(month);

  const handleDayClick = (day, { selected }) => {
    setValue(selected ? '' : day);
    close && close();
  };

  const enterDownMenu = () => {
    if (timer) clearTimeout(timer);

    !isOpen && open && open();
  };

  const leaveDownMenu = () => {
    if (timer) clearTimeout(timer);

    if (!isAlwaysOpened && closeOnLeave && close) {
      setTimer(setTimeout(() => {
        close();
      }, 350));
    }
  };

  const content = (
    <div className="date-picker" onMouseEnter={enterDownMenu} onMouseLeave={leaveDownMenu} ref={$picker.current}>
      <DayPicker
        selectedDays={value}
        onDayClick={handleDayClick}
        month={month}
        fromMonth={fromMonth}
        toMonth={toMonth}
        captionElement={({ date, localeUtils }) => (
          <YearMonthForm date={date} localeUtils={localeUtils} onChange={handleYearMonthChange} />
        )}
      />
      {
        showNavbar && (
          <button className="DayPicker-NavBar" onClick={close}>
            <CrossLargeSVG />
          </button>
        )
      }
    </div>
  );

  if (parentSelector && $parent.current) {
    return ReactDOM.createPortal(content, $parent.current);
  }

  return content;
};

Picker.propTypes = {
  isOpen: PT.bool,
  value: PT.any,
  close: PT.func,
  open: PT.func,
  setValue: PT.func,
  error: PT.string,
};

const DatePicker = ({ fieldProps, isAlwaysOpened, onClose, error, parentSelector, closeOnLeave, showNavbar }) => {
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);

  const handleChange = (value) => fieldProps.onChange(formatDate(value, 'YYYY-MM-DD'));

  const togglePicker = () => setIsDatePickerOpen(!isDatePickerOpen);
  const openPicker = () => setIsDatePickerOpen(true);
  const closePicker = () => {
    setIsDatePickerOpen(false);
    onClose && onClose();
  };

  useEffect(() => {
    const selectedDay = fieldProps.value ? moment(fieldProps.value, 'YYYY-MM-DD').toDate() : null;

    setSelectedDay(selectedDay);
  }, [fieldProps.value]);

  return (
    <div className="date-picker-field">
      <div className={cn('date-picker-field__input', 'date-picker-field__input_single')}>
        {!isAlwaysOpened && (
          <button
            className={cn('date-picker-field__btn', {
              'date-picker-field__btn_active': isDatePickerOpen,
              'date-picker-field__btn_error': !!error,
            })}
            onClick={togglePicker}
            type="button"
          >
            {selectedDay ? moment(selectedDay).format('YYYY-MM-DD') : fieldProps.placeholder}
            <CalendarLargeSVG />
          </button>
        )}
        {(isDatePickerOpen || isAlwaysOpened) && (
          <Picker
            value={selectedDay}
            close={closePicker}
            open={openPicker}
            setValue={handleChange}
            isOpen={isDatePickerOpen}
            isAlwaysOpened={isAlwaysOpened}
            parentSelector={parentSelector}
            closeOnLeave={closeOnLeave}
            showNavbar={showNavbar}
          />
        )}
      </div>
    </div>
  );
};

DatePicker.propTypes = {
  pickers: PT.arrayOf(PT.object),
};

export default DatePicker;
